<template>
  <div class="hm-interview-evaluation">
    <div class="hm-score-card">
      <div class="hm-score-title">
        <div>{{ $t(scoreTitleI18nKey) }}</div>
      </div>

      <div class="hm-score-body">
        <div v-for="hm in evaluation.hm_scores" :key="hm.id" class="hm-score-row" :class="getHmStatusClass(hm)">
          <div class="hm-row-summary" @click="toggleHm(hm.id)">
            <span class="hm-summary-main">
              <span class="hm-name">{{ hm.name }}</span>
              <span v-if="hm.recommend" class="recommend-label">{{ getHmRecommendLabel(hm) }}</span>
              <span v-if="hm.comment" class="recommend-comment">{{ hm.comment }}</span>
            </span>
            <span class="hm-score-action">
              <span v-if="shouldShowCompletion(hm)" class="completion-text">
                <span>{{ $t(`${i18nPrefix}.label.ratingCompletion`) }}: </span>
                <span class="completion-value">{{ hm.completion }}%</span>
              </span>
              <span class="score-control">
                <RecommendationResultChart
                  hiddenTip
                  class="score-circle"
                  ringBackgroundColor="#E4E7ED"
                  :decision="hm.decision"
                  :score="hm.score"
                  :completion="hm.completion"
                  :fitScoreStyle="scoreChartStyle"
                  :chartOption="scoreChartOption"
                  :circleWidth="3"
                  :fontSize="16"
                />
                <i v-if="hm.has_questionnaire" class="el-icon-arrow-down" :class="isExpanded(hm.id) && 'is-expanded'"></i>
              </span>
            </span>
          </div>

          <div v-if="isExpanded(hm.id) && hm.questions && hm.questions.length" class="question-list">
            <div v-for="question in hm.questions" :key="question.id" class="question-card">
              <div class="question-title">
                <div class="question-name">
                  <span class="question-index">
                    <span>{{ getQuestionNumberLabel(question) }}</span>
                    <span v-if="question.required" class="question-required">*</span>
                    <span>:</span>
                  </span>
                  <span class="question-text">{{ question.title }}</span>
                </div>
                <el-tag class="question-type" size="mini" effect="plain">
                  <i :class="getQuestionTypeIcon(question.type)"></i>
                  {{ getQuestionTypeLabel(question.type) }}
                </el-tag>
              </div>
              <div v-if="question.weight" class="question-weight">
                {{ $t(`${i18nPrefix}.label.weighting`, { value: formatQuestionWeight(question.weight) }) }}
              </div>

              <div class="answer-content">
                <template v-if="question.type === HM_QUESTION_TYPE.RATING">
                  <div class="answer-line">
                    <div class="answer-label scale-answer-label">{{ $t(`${i18nPrefix}.label.scale`) }}:</div>
                    <div class="rating-scale">
                      <span
                        v-for="option in question.scale_options"
                        v-auto-tooltip.always="getScaleOptionTooltip(option)"
                        :key="option.value"
                        class="scale-number"
                        :class="isScaleOptionActive(option, question) && 'is-active'"
                      >
                        {{ option.value }}
                      </span>
                      <span v-if="question.scale_text" class="scale-summary">{{ question.scale_text }}</span>
                    </div>
                  </div>
                  <div class="answer-line">
                    <div class="answer-label">{{ $t(`${i18nPrefix}.label.comments`) }}:</div>
                    <div class="answer-text">{{ question.comment || '-' }}</div>
                  </div>
                </template>

                <template v-else-if="question.type === HM_QUESTION_TYPE.SINGLE">
                  <div class="answer-line">
                    <div class="answer-label">{{ $t(`${i18nPrefix}.label.answer`) }}:</div>
                    <el-radio-group v-if="hasSingleAnswer(question)" class="option-list radio-options" :value="getSingleAnswerValue(question)" disabled>
                      <el-radio :label="getSingleAnswerValue(question)">{{ getSingleAnswerValue(question) }}</el-radio>
                    </el-radio-group>
                    <div v-else class="answer-text">-</div>
                  </div>
                </template>

                <template v-else-if="question.type === HM_QUESTION_TYPE.MULTIPLE">
                  <div class="answer-line answer-line-top">
                    <div class="answer-label">{{ $t(`${i18nPrefix}.label.answer`) }}:</div>
                    <el-checkbox-group v-if="hasMultipleAnswers(question)" class="option-list multi-options" :value="getMultipleAnswerValues(question)" disabled>
                      <el-checkbox
                        v-for="option in getMultipleAnswerValues(question)"
                        :key="option"
                        :label="option"
                      >{{ option }}</el-checkbox>
                    </el-checkbox-group>
                    <div v-else class="answer-text">-</div>
                  </div>
                </template>

                <template v-else>
                  <div class="answer-line answer-line-top">
                    <div class="answer-label">{{ $t(`${i18nPrefix}.label.answer`) }}:</div>
                    <div class="answer-text">{{ question.answer || '-' }}</div>
                  </div>
                </template>

                <div v-if="hasQuestionAnswer(question)" class="answer-meta">
                  <span>{{ formatDate(question.submitted_time) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { HM_QUESTION_TYPE } from '@/constant/questionnaire'
import RecommendationResultChart from '@/views/adminNew/components/recommendationResultChart.vue'

export default {
  components: {
    RecommendationResultChart
  },
  props: {
    evaluation: {
      type: Object,
      required: true
    },
    resultType: {
      type: String,
      default: 'rating'
    }
  },
  computed: {
    scoreTitleI18nKey () {
      const key = this.resultType === 'interview' ? 'hmInterviewResult' : 'hmRatingResult'
      return `${this.i18nPrefix}.label.${key}`
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      HM_QUESTION_TYPE,
      expandedHmIds: [],
      scoreChartStyle: {
        width: '40px',
        height: '40px'
      },
      scoreChartOption: {
        series: {
          data: [{
            title: {
              fontWeight: 500
            }
          }]
        }
      }
    }
  },
  methods: {
    toggleHm (hmId) {
      if (this.isExpanded(hmId)) {
        this.expandedHmIds = this.expandedHmIds.filter(item => item !== hmId)
      } else {
        this.expandedHmIds = [...this.expandedHmIds, hmId]
      }
    },
    isExpanded (hmId) {
      return this.expandedHmIds.includes(hmId)
    },
    hasScore (score) {
      return ![null, undefined, ''].includes(score) && Number.isFinite(Number(score))
    },
    formatDate (date) {
      return date ? moment(date).format('YYYY/MM/DD') : '-'
    },
    formatQuestionWeight (weight) {
      const numericWeight = Number(weight)
      return Number.isFinite(numericWeight) ? Math.trunc(numericWeight) : weight
    },
    getQuestionNumberLabel (question) {
      return this.$t(`${this.i18nPrefix}.label.questionNumber`, { number: question.number }).replace(/[:：]\s*$/, '')
    },
    getHmRecommendLabel (hm) {
      return this.$t(`${this.i18nPrefix}.label.${hm.recommend}`)
    },
    getHmStatusClass (hm) {
      const statusClassMap = {
        RECOMMENDED: 'is-recommended',
        NOT_RECOMMENDED: 'is-not-recommended',
        NO_RECOMMENDATION: 'is-no-recommendation',
        SPLIT_DECISION: 'is-recommended',
        RATE_PENDING: 'is-pending'
      }
      return statusClassMap[hm.recommend]
    },
    shouldShowCompletion (hm) {
      return Boolean(hm && hm.has_questionnaire && hm.has_rating_question)
    },
    hasQuestionAnswer (question) {
      if (!question) {
        return false
      }
      if (question.type === HM_QUESTION_TYPE.RATING) {
        return ![null, undefined, ''].includes(question.score)
      }
      if (question.type === HM_QUESTION_TYPE.SINGLE) {
        return this.hasSingleAnswer(question)
      }
      if (question.type === HM_QUESTION_TYPE.MULTIPLE) {
        return this.hasMultipleAnswers(question)
      }
      return ![null, undefined, '', '-'].includes(question.answer)
    },
    hasSingleAnswer (question) {
      return Boolean(question && ![null, undefined, '', '-'].includes(question.answer))
    },
    getSingleAnswerValue (question) {
      return question.answer
    },
    hasMultipleAnswers (question) {
      return Boolean(question && question.answer_options && question.answer_options.length)
    },
    getMultipleAnswerValues (question) {
      return question.answer_options
    },
    getScaleOptionTooltip (option) {
      const content = option.description
      return {
        content,
        disabled: !content
      }
    },
    isScaleOptionActive (option, question) {
      return this.hasScore(question.score) && Number(option.value) === Number(question.score)
    },
    getQuestionTypeLabel (type) {
      const typeMap = {
        [HM_QUESTION_TYPE.RATING]: 'rating',
        [HM_QUESTION_TYPE.TEXT]: 'text',
        [HM_QUESTION_TYPE.SINGLE]: 'singleSelect',
        [HM_QUESTION_TYPE.MULTIPLE]: 'multiSelect',
        [HM_QUESTION_TYPE.DATE]: 'date'
      }
      return this.$t(`${this.i18nPrefix}.label.hmEvaluationQuestionType.${typeMap[type]}`)
    },
    getQuestionTypeIcon (type) {
      const iconMap = {
        [HM_QUESTION_TYPE.RATING]: 'icona-Star2Stroke',
        [HM_QUESTION_TYPE.TEXT]: 'iconText',
        [HM_QUESTION_TYPE.SINGLE]: 'icona-SingleSelect',
        [HM_QUESTION_TYPE.MULTIPLE]: 'icona-multipleanswer1',
        [HM_QUESTION_TYPE.DATE]: 'iconCalendar1'
      }
      return ['iconfont', iconMap[type]]
    }
  }
}
</script>

<style lang="scss" scoped>
.hm-interview-evaluation {
  color: #606266;

  .hm-score-card {
    overflow: hidden;
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    background: #fff;

    .hm-score-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 20px 15px;
      border-bottom: 1px solid #DCDFE6;
      color: #909399;
      background: #F9F9F9;
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
    }

    .hm-score-body {
      display: flex;
      flex-direction: column;
      gap: 0;
      background: #fff;

      .hm-score-row {
        --score-color: #51BF82;

        border-bottom: 1px solid #DCDFE6;
        background: #fff;

        &:last-child {
          border-bottom: 0;
        }

        &.is-pending {
          --score-color: #F4A958;
        }

        &.is-not-recommended {
          --score-color: #E27676;
        }

        &.is-no-recommendation {
          --score-color: #F4A958;
        }

        .hm-row-summary {
          display: flex;
          flex-direction: column;
          width: 100%;
          padding: 14px 20px 0;
          border: 0;
          color: #606266;
          background: #fff;
          cursor: pointer;
          text-align: left;
          font-size: 14px;
          line-height: 20px;

          .hm-summary-main {
            display: flex;
            flex-direction: column;
            width: 100%;
            min-width: 0;
            min-height: 34px;

            .hm-name {
              color: #303133;
              font-size: 14px;
              line-height: 20px;
              font-weight: 500;
            }

            .recommend-label {
              margin-top: 4px;
              color: var(--score-color);
              font-size: 14px;
              line-height: 16px;

              &:last-child {
                padding-bottom: 12px;
              }
            }

            .recommend-comment {
              margin-top: 4px;
              padding-bottom: 12px;
              color: #606266;
              font-size: 13px;
              line-height: 20px;
              white-space: pre-wrap;
              word-break: break-word;
            }
          }

          .hm-score-action {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding: 10px 0;
            border-top: 1px solid #F0F0F0;

            .completion-text {
              color: #909399;
              font-size: 13px;
              line-height: 40px;

              .completion-value {
                color: #606266;
              }
            }

            .score-control {
              display: flex;
              align-items: center;
              gap: 16px;
              margin-left: auto;

              .score-circle {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                color: #606266;
                font-size: 16px;
                line-height: 16px;
                font-weight: 500;
              }

              .el-icon-arrow-down {
                color: #606266;
                font-size: 16px;
                transition: transform .3s;

                &.is-expanded {
                  transform: rotate(180deg);
                }
              }
            }
          }
        }

        .question-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px 20px;
          background: #FAFAFA;

          .question-card {
            overflow: hidden;
            border: 1px solid #DCDFE6;
            border-radius: 4px;
            background: #fff;

            .question-title {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 24px;
              padding: 16px 20px 8px;
              background: #FAFAFA;

              .question-name {
                display: flex;
                align-items: flex-start;
                gap: 40px;
                min-width: 0;
                font-size: 16px;
                line-height: 16px;

                .question-index {
                  flex: 1 0 90px;

                  .question-required {
                    color: #E34D59;
                  }
                }

                .question-text {
                  min-width: 0;
                  font-weight: 500;
                  line-height: 20px;
                  word-break: break-word;
                  white-space: pre-wrap;
                }
              }

              .question-type.el-tag {
                display: flex;
                align-items: center;
                gap: 4px;
                flex-shrink: 0;
                height: auto;
                padding: 4px 8px;
                border: 1px solid #CDECF7;
                border-radius: 21px;
                color: #0096CC;
                background: #F1FBFF;
                font-size: 10px;
                line-height: 10px;
                font-weight: 500;

                .iconfont {
                  font-size: 12px;
                }
              }
            }

            .question-weight {
              padding: 0 20px 16px 150px;
              color: #0096CC;
              background: #FAFAFA;
              font-size: 12px;
              line-height: 12px;
            }

            .answer-content {
              padding: 16px 20px;
              font-size: 14px;
              line-height: 20px;

              .answer-line {
                display: flex;
                gap: 40px;

                & + .answer-line {
                  margin-top: 14px;
                }

                &.answer-line-top {
                  align-items: flex-start;
                }

                .answer-label {
                  width: 90px;
                  flex-shrink: 0;

                  &.scale-answer-label {
                    line-height: 28px;
                  }
                }

                .answer-text {
                  flex: 1;
                  white-space: pre-wrap;
                }

                .rating-scale {
                  display: flex;
                  align-items: center;
                  flex-wrap: wrap;
                  gap: 9px;

                  .scale-number {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    border: 1px solid #0096CC;
                    border-radius: 50%;
                    color: #0096CC;
                    font-size: 16px;
                    line-height: 16px;
                    font-weight: 500;

                    &.is-active {
                      color: #fff;
                      background: #0096CC;
                    }
                  }

                  .scale-summary {
                    color: #606266;
                    font-size: 14px;
                    line-height: 20px;
                  }
                }

                .option-list {
                  display: flex;
                  align-items: center;
                  gap: 8px;
                }

                .radio-options {
                  ::v-deep .el-radio {
                    margin-right: 0;
                    color: #606266;
                    line-height: 20px;
                  }

                  ::v-deep .el-radio__label {
                    color: #606266;
                    font-size: 14px;
                    line-height: 20px;
                  }

                  ::v-deep .el-radio__input.is-disabled.is-checked .el-radio__inner {
                    border-color: #0096CC;
                    background: #0096CC;
                  }

                  ::v-deep .el-radio__input.is-disabled.is-checked .el-radio__inner::after {
                    background: #fff;
                  }

                  ::v-deep .el-radio__input.is-disabled + span.el-radio__label {
                    color: #606266;
                  }
                }

                .multi-options {
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 4px;

                  ::v-deep .el-checkbox {
                    margin-right: 0;
                    color: #606266;
                    line-height: 20px;
                  }

                  ::v-deep .el-checkbox__label {
                    color: #606266;
                    font-size: 14px;
                    line-height: 20px;
                  }

                  ::v-deep .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner {
                    border-color: #0096CC;
                    background: #0096CC;
                  }

                  ::v-deep .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner::after {
                    border-color: #fff;
                  }

                  ::v-deep .el-checkbox__input.is-disabled + span.el-checkbox__label {
                    color: #606266;
                  }
                }
              }

              .answer-meta {
                display: flex;
                gap: 8px;
                margin-top: 16px;
                color: #909399;
                font-size: 12px;
                line-height: 12px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
